import { Divider } from "@mui/material";

import PrimaryButton from "../../../Components/Buttons/primary-button";
import { durationOptions, frequencyOptions } from "../../../Pages/Rent Payment";

import renderCancelRentPayment from "../../Modals/Pay Rent/cancel-rent-payment";

import { useModalContext } from "../../../Contexts/ModalContext";

import { useStakeDebit } from "../../../CustomHooks/Queries";

import { capitaliseString, formatCurrency, formatDate, getNumberWithOrdinal } from "../../../Utils/helpers";
import { longMonthDateFormat, modalVariants } from "../../../Utils/constants";

import { CheckCircleWithShadow } from "../../../Assets/Icons";

export default function renderRentPaymentDetails(modalContext, props) {
  const { updateModalComponent, pushModalStack, setModalVariant, setModalTitle, setModalClassName } = modalContext;
  const { isAPIResponseStatus = false } = props;

  updateModalComponent(() => <RentPaymentDetails {...props} />);
  pushModalStack(() => renderRentPaymentDetails(modalContext, props));
  setModalVariant(modalVariants.flexDialog);
  setModalTitle("Payment Details");
  setModalClassName(`no-full-screen${isAPIResponseStatus ? " hide-header" : ""}`);
}

export const RentPaymentDetails = (props) => {
  const { isAPIResponseStatus = false, isRecurringTransaction, isEditing, paymentDetails = {} } = props;
  const {
    amount,
    status,
    statusDisplayName,
    statusType,
    created_at,
    estimated_arrival,
    StatusIcon,
    notes,
    type,
    payee,
    to,
    reason,
    fees,
    frequency_day,
    frequency_type,
    duration,
    number_of_payments,
    start_date,
    end_date,
    last_payment_date,
  } = paymentDetails;

  const modalContext = useModalContext();
  const { onModalClose } = modalContext;

  const { data: stakeDebit } = useStakeDebit();

  const handleCTAClick = () => {
    if (isAPIResponseStatus) {
      onModalClose();
    } else {
      renderCancelRentPayment(modalContext, paymentDetails);
    }
  };

  const canCancelPayment = ["Pending", "Upcoming"].includes(status);

  const hasCTAButton = isAPIResponseStatus || canCancelPayment;

  return (
    <>
      <div className={`modal-dialog-content${hasCTAButton ? "" : " mb-6"}`}>
        {isAPIResponseStatus && <CheckCircleWithShadow className="modal-status-icon my-6" />}
        <h3 className={`${!isAPIResponseStatus ? "text-base-medium" : "text-lg-medium"} color--mist text-center mb-2`}>
          {isAPIResponseStatus
            ? isRecurringTransaction
              ? `Recurring Payment ${isEditing ? "Updated" : "Created"}`
              : "Payment Submitted"
            : "Amount"}
        </h3>
        <div className="text-4xl-semibold text-center color--mist">{formatCurrency(amount)}</div>
        {!isAPIResponseStatus && (
          <div className={`transfer-status-badge borderless ${statusType} mt-2`}>
            <StatusIcon /> {statusDisplayName}
          </div>
        )}
        {!!reason && (
          <span
            className={`text-xs-regular text-center color--${statusType === "negative" ? "error-red" : "mist"} mt-2`}
          >
            {reason}
          </span>
        )}
        <Divider className="my-4" sx={{ borderBottom: "var(--card-border)", opacity: 1 }} />
        <div className="transfer-detail">
          <span className="detail-label">To{isAPIResponseStatus && " Account"}</span>
          <span className="detail-value">{to}</span>
        </div>
        <div className="transfer-detail">
          <span className="detail-label">Payee</span>
          <span className="detail-value">{payee}</span>
        </div>
        {!!notes && (
          <div className="transfer-detail">
            <span className="detail-label">Note</span>
            <span className="detail-value">{notes}</span>
          </div>
        )}
        {!isAPIResponseStatus && (
          <div className="transfer-detail">
            <span className="detail-label">From</span>
            <span className="detail-value">Stake Checking · {stakeDebit?.data?.data?.ssn_last_4}</span>
          </div>
        )}
        {!isRecurringTransaction ? (
          <>
            {isAPIResponseStatus && (
              <div className="transfer-detail">
                <span className="detail-label">Estimated Arrival</span>
                <span className="detail-value">{formatDate(estimated_arrival, longMonthDateFormat)}</span>
              </div>
            )}
            <div className="transfer-detail">
              <span className="detail-label">Date</span>
              <span className="detail-value">{formatDate(created_at, longMonthDateFormat)}</span>
            </div>
          </>
        ) : (
          <>
            <div className="transfer-detail">
              <span className="detail-label">Frequency</span>
              <span className="detail-value">
                {capitaliseString(frequency_type)} on{" "}
                {frequency_type === frequencyOptions[2].value
                  ? `the ${getNumberWithOrdinal(frequency_day)}`
                  : capitaliseString(frequency_day)}
              </span>
            </div>
            {duration === durationOptions[1].value && (
              <div className="transfer-detail">
                <span className="detail-label">Number of Payments</span>
                <span className="detail-value">{number_of_payments}</span>
              </div>
            )}
            <div className="transfer-detail">
              <span className="detail-label">Start Date</span>
              <span className="detail-value">{formatDate(start_date, longMonthDateFormat)}</span>
            </div>
            {duration === durationOptions[2].value && (
              <div className="transfer-detail">
                <span className="detail-label">Last Payment Date</span>
                <span className="detail-value">{formatDate(last_payment_date, longMonthDateFormat)}</span>
              </div>
            )}
            <div className="transfer-detail">
              <span className="detail-label">End Date</span>
              <span className="detail-value">
                {duration === durationOptions[0].value
                  ? durationOptions[0].label
                  : formatDate(end_date, longMonthDateFormat)}
              </span>
            </div>
          </>
        )}
        {!isAPIResponseStatus && (
          <>
            <div className="transfer-detail">
              <span className="detail-label">Fees</span>
              <span className="detail-value">{formatCurrency(fees)}</span>
            </div>
            <div className="transfer-detail">
              <span className="detail-label">Type</span>
              <span className="detail-value">{type}</span>
            </div>
          </>
        )}
      </div>
      {hasCTAButton && (
        <div className="action-container">
          <PrimaryButton
            onClick={handleCTAClick}
            {...(!isAPIResponseStatus && canCancelPayment ? { className: "red-theme" } : {})}
          >
            {isAPIResponseStatus ? "Done" : "Cancel Payment"}
          </PrimaryButton>
        </div>
      )}
    </>
  );
};
